import { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { LayoutDashboard, Package, Users, LogOut, Menu, X, Building2, BarChart3, UsersRound, MessageCircle, Truck, LifeBuoy, GraduationCap, Zap, Calculator, Settings, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import BrandLogo from '@/components/BrandLogo';

const navItems = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/admin/loads', icon: Package, label: 'Loads' },
  { to: '/admin/dispatch', icon: Zap, label: 'Dispatch' },
  { to: '/admin/drivers', icon: Users, label: 'Drivers' },
  { to: '/admin/customers', icon: UsersRound, label: 'Customers' },
  { to: '/admin/equipment', icon: Truck, label: 'Equipment' },
  { to: '/admin/tickets', icon: LifeBuoy, label: 'Tickets' },
  { to: '/admin/training', icon: GraduationCap, label: 'Training' },
  { to: '/admin/accounting', icon: Calculator, label: 'Accounting' },
  { to: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
  { to: '/admin/settings', icon: Settings, label: 'Settings' },
];

export default function AdminLayout() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [msgCount, setMsgCount] = useState(0);
  const [userMenu, setUserMenu] = useState(false);

  const fetchMsgCount = useCallback(() => {
    if (!token) return;
    api.get('/messages/unread-count')
      .then(r => setMsgCount(r.data?.unread || 0))
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    fetchMsgCount();
    const iv = setInterval(fetchMsgCount, 30000);
    return () => clearInterval(iv);
  }, [fetchMsgCount]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className={`${collapsed ? 'w-16' : 'w-56'} bg-sidebar border-r border-sidebar-border flex flex-col transition-all duration-200 shrink-0`}
        data-testid="admin-sidebar">
        <div className="p-4 flex items-center justify-between">
          {!collapsed && <BrandLogo size="sm" />}
          <Button variant="ghost" size="icon" onClick={() => setCollapsed(!collapsed)} className="text-muted-foreground hover:text-foreground h-8 w-8"
            data-testid="sidebar-toggle">
            {collapsed ? <Menu className="w-4 h-4" /> : <X className="w-4 h-4" />}
          </Button>
        </div>

        <nav className="flex-1 py-4 space-y-1 px-2 overflow-y-auto">
          {navItems.map(item => (
            <NavLink key={item.to} to={item.to} end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive ? 'bg-sidebar-active text-sidebar-active-foreground border border-brand-accent/20' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50 border border-transparent'
                }`
              }
              data-testid={`nav-${item.label.toLowerCase()}`}>
              <item.icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-sidebar-border">
          {!collapsed && (
            <div className="flex items-center gap-2 mb-3 px-2">
              <Building2 className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
              <span className="text-xs text-muted-foreground truncate">{user?.org_id}</span>
            </div>
          )}
          <Button variant="ghost" onClick={handleLogout}
            className="w-full flex items-center gap-2 text-muted-foreground hover:text-red-400 justify-start text-sm"
            data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
            {!collapsed && <span>Sign Out</span>}
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-sidebar border-b border-sidebar-border px-6 py-3 flex items-center justify-end gap-4 shrink-0"
          data-testid="admin-header">
          <button onClick={() => navigate('/admin/messages')}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-muted transition-colors"
            data-testid="admin-messages-btn">
            <MessageCircle className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground font-medium">Messenger</span>
            {msgCount > 0 && (
              <span className="bg-brand-accent text-brand-accent-foreground text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
                data-testid="message-badge">{msgCount > 99 ? '99+' : msgCount}</span>
            )}
          </button>
          <div className="relative">
            <button onClick={() => setUserMenu(!userMenu)}
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted transition-colors">
              <div className="w-8 h-8 rounded-full bg-brand-accent/20 flex items-center justify-center text-sm font-bold text-brand-accent">
                {user?.name?.charAt(0)?.toUpperCase() || 'A'}
              </div>
              <span className="text-sm text-muted-foreground">{user?.name}</span>
              <ChevronDown className="w-3 h-3 text-muted-foreground" />
            </button>
            {userMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setUserMenu(false)} />
                <div className="absolute right-0 top-full mt-1 w-44 bg-card border border-border rounded-lg shadow-xl z-50 py-1">
                  <button onClick={() => { setUserMenu(false); navigate('/admin/settings'); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                    <Settings className="w-3.5 h-3.5" strokeWidth={1.5} /> Settings
                  </button>
                  <div className="border-t border-border my-1" />
                  <button onClick={() => { setUserMenu(false); handleLogout(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-red-400 hover:bg-muted transition-colors">
                    <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> Sign Out
                  </button>
                </div>
              </>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-auto">
          <div className="p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
